import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, RotateCcw, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export const ARCHIVE_AFTER_DAYS = 2;
export const DELETE_AFTER_DAYS = 14;

export const daysUntil = (completedAt, afterDays) => {
  if (!completedAt) return null;
  const t = new Date(completedAt).getTime();
  if (Number.isNaN(t)) return null;
  const elapsed = (Date.now() - t) / 86400000;
  return Math.max(0, Math.ceil(afterDays - elapsed));
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export function SupportArchive({ canAdmin, onChanged }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/support/tickets?archived=1`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Load failed');
      setRows(await res.json());
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reopen = async (t) => {
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${t.id}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'awaiting_ox' }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Reopen failed');
      setRows((cur) => cur.filter((x) => x.id !== t.id));
      toast.success(`${t.reference || 'Request'} back on the board → Awaiting Ox Action`);
      onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.reference || 'this request'} now? It will be removed from the archive.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${t.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      setRows((cur) => cur.filter((x) => x.id !== t.id));
      toast.success('Request deleted');
      onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Card className="p-10 text-center text-gray-500">Loading archive…</Card>;

  return (
    <Card className="overflow-hidden" data-testid="support-archive">
      <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2 text-sm text-slate-700">
        <Archive className="w-4 h-4" />
        <span className="font-semibold">{rows.length} archived</span>
        <span className="text-slate-500 hidden sm:inline">· completed queries move here after {ARCHIVE_AFTER_DAYS} days and are permanently deleted {DELETE_AFTER_DAYS} days after completion</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-10 text-center text-gray-500 text-sm" data-testid="support-archive-empty">Nothing in the archive yet.</div>
      ) : (
        <ul className="divide-y">
          {rows.map((t) => {
            const left = daysUntil(t.completed_at, DELETE_AFTER_DAYS);
            return (
              <li key={t.id} className="px-4 py-3 flex flex-wrap items-center gap-3 hover:bg-slate-50" data-testid={`support-archive-row-${t.id}`}>
                <button type="button" onClick={() => navigate(`/staff/support/${t.id}`)} className="flex-1 min-w-[200px] text-left" data-testid={`support-archive-open-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-gray-500">{t.reference}</span>
                    <span className="font-semibold text-sm text-gray-900 truncate">{t.title || 'Support'}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{t.installer_name || 'No installer'}{t.assigned_to_name ? ` · ${t.assigned_to_name}` : ''}</div>
                </button>
                <div className="text-xs text-slate-600 inline-flex items-center gap-1 shrink-0" title={t.completed_at}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Completed {fmtDate(t.completed_at)}
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${left !== null && left <= 3 ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-slate-100 text-slate-700'}`} data-testid={`support-archive-deletes-${t.id}`}>
                  {left === null ? 'No completion date' : left === 0 ? 'Deletes within the hour' : `Deletes in ${left}d`}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => reopen(t)} data-testid={`support-archive-reopen-${t.id}`} title="Put back on the board">
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                  </Button>
                  {canAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-rose-600 hover:bg-rose-50" data-testid={`support-archive-delete-${t.id}`} title="Delete now">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
